/**
 * Read-tool schemas + dispatcher for the 4CM Coach Brain.
 *
 * Each tool maps to a Supabase query. Return shapes are kept small (ids + key fields)
 * so Claude spends its token budget reasoning, not parroting columns. The model
 * re-requests detail by calling get_* after list_*.
 */
import { supabase } from '@4cm/core/supabase';
import type { AnthropicTool, ToolDispatcher } from '@4cm/core/ai';
import { searchKnowledge } from '@4cm/core/knowledgeSearch';
import type { TacticsLayout } from '@4cm/core/tactics';
import { searchMemories } from './brainMemory';
import {
  ALL_TACTICS_TOOLS as TACTICS_TOOLS,
  critiqueTactics,
  dispatchPatternTool,
  dispatchPlayerBriefsTool,
  type TacticsPatternStore,
} from './tactics';

export interface BrainToolCtx {
  coachId: string;
  teamId: string | null;
  /**
   * Lazy accessor for the active tactics layout on the current
   * surface. Populated by the app when the coach is on a tactics
   * page; the critique tool uses it to inspect geometry without
   * requiring the model to echo the full layout through tool args.
   * Returns `null` when no board is active or storage is empty.
   */
  getTacticsLayout?: () => TacticsLayout | null;
  /**
   * Lazy accessor for the Phase 4 tactics pattern library adapter.
   * Surface packages (apps/web2) supply their localStorage-backed
   * store at dispatcher creation so this central module stays
   * apps-free. Returns `null` when running outside the browser (e.g.
   * a server-side context where the pattern library is unavailable).
   */
  getTacticsPatternStore?: () => TacticsPatternStore | null;
}

/** Demo coach UUID — matches the seed in migration 00009. */
export const DEMO_COACH_ID = 'a0000000-0000-0000-0000-000000000020';

// ---------------------------------------------------------------------------
// Read-tool schemas
// ---------------------------------------------------------------------------

export const READ_TOOLS: AnthropicTool[] = [
  {
    name: 'get_team',
    description: 'Return the coach\'s current team (name, age group, formation, season dates, player_count). ' +
      'Call this first whenever the coach asks anything about "my team".',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'list_players',
    description: 'List the team roster. Returns id, name, position, jersey_number for each player.',
    input_schema: {
      type: 'object',
      properties: {
        team_id: { type: 'string', description: 'Optional override. Defaults to active team.' },
      },
    },
  },
  {
    name: 'get_player',
    description: 'Full player detail: profile + aggregated corner ratings + recent development entries. ' +
      'Use after list_players when the coach asks about a specific player.',
    input_schema: {
      type: 'object',
      properties: { player_id: { type: 'string' } },
      required: ['player_id'],
    },
  },
  {
    name: 'list_sessions',
    description: 'List training sessions in a date range (most recent first).',
    input_schema: {
      type: 'object',
      properties: {
        team_id: { type: 'string' },
        from: { type: 'string', description: 'ISO date, inclusive (YYYY-MM-DD)' },
        to: { type: 'string', description: 'ISO date, inclusive (YYYY-MM-DD)' },
        limit: { type: 'number', description: 'Default 10, max 50' },
      },
    },
  },
  {
    name: 'get_session',
    description: 'Session detail with its drills, objectives, and corner coverage.',
    input_schema: {
      type: 'object',
      properties: { session_id: { type: 'string' } },
      required: ['session_id'],
    },
  },
  {
    name: 'list_drills',
    description: 'Filter the drill library by corner, type, or free-text.',
    input_schema: {
      type: 'object',
      properties: {
        corner_id: { type: 'string' },
        drill_type: { type: 'string', description: 'WARM_UP | TECHNICAL | TACTICAL | SMALL_SIDED_GAME | COOL_DOWN' },
        search: { type: 'string', description: 'Fuzzy match on name or description' },
        limit: { type: 'number', description: 'Default 15, max 40' },
      },
    },
  },
  {
    name: 'get_ratings',
    description: 'Coach-assigned ratings for a player. Returns raw rows + computed averages by corner.',
    input_schema: {
      type: 'object',
      properties: {
        player_id: { type: 'string' },
        corner_id: { type: 'string', description: 'Optional; filter to one corner' },
        since: { type: 'string', description: 'Optional ISO date' },
      },
      required: ['player_id'],
    },
  },
  {
    name: 'list_video_tags',
    description: 'Tagged video clips. Filter by player or session.',
    input_schema: {
      type: 'object',
      properties: {
        player_id: { type: 'string' },
        session_id: { type: 'string' },
        since: { type: 'string' },
        limit: { type: 'number', description: 'Default 10' },
      },
    },
  },
  {
    name: 'get_curriculum',
    description: 'The active season curriculum for a team (phase themes, weekly topics).',
    input_schema: {
      type: 'object',
      properties: { team_id: { type: 'string' } },
    },
  },
  {
    name: 'get_principles',
    description: 'Coach\'s philosophy principles grouped by type (attack / defend / transition / restart).',
    input_schema: {
      type: 'object',
      properties: { team_id: { type: 'string' } },
    },
  },
  {
    name: 'get_wellness',
    description: 'Player wellness logs (sleep, mood, soreness, RPE). Coach-visible welfare data — handle with care.',
    input_schema: {
      type: 'object',
      properties: {
        player_id: { type: 'string' },
        since: { type: 'string' },
      },
      required: ['player_id'],
    },
  },
  {
    name: 'get_session_review_context',
    description: 'Everything the Brain needs to walk the coach through a post-session review: ' +
      'the session + its drills (with session_drill ids for grading), the roster, attendance so far, ' +
      'any existing review row, the coach\'s own player grades so far, and any player self-feedback ' +
      'submitted from mobile. Call this first when the coach asks to review a session.',
    input_schema: {
      type: 'object',
      properties: { session_id: { type: 'string' } },
      required: ['session_id'],
    },
  },
  {
    name: 'search_memories',
    description: 'Search the Brain\'s own long-term memory of this coach. Use when the coach says ' +
      '"do you remember...", "what do you know about...", or when you need to recall a prior preference ' +
      'the coach has taught you. Returns durable facts the Brain has previously learned.',
    input_schema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Fuzzy text to match against fact column' },
        scope: { type: 'string', description: 'coach | team | player | session | drill' },
        entity_id: { type: 'string', description: 'Filter to one specific player/session/drill' },
        tags: { type: 'array', items: { type: 'string' }, description: 'e.g. ["corner:psychological"]' },
        limit: { type: 'number', description: 'Default 8' },
      },
    },
  },
  {
    name: 'search_knowledge',
    description: 'Search the curated coaching KnowledgeBase for passages relevant to the coach\'s question. ' +
      'Covers frameworks (FA 4 Corner Model, Harwood 5Cs, Ben Football System, YouthDev, LTAD, Tactical ' +
      'Periodisation, Constraint-Led Approach, Belgian + Dutch + England DNA models, Horst Wein, Talent ID). ' +
      'Use when the coach asks for a concept explanation, a framework comparison, or age-phase guidance. ' +
      'Returns compact hits with source citation and score. Cite the source in your reply.',
    input_schema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Natural-language query — what you want to find in the knowledge base.' },
        match_count: { type: 'number', description: 'How many hits to return. Default 5, max 10.' },
      },
      required: ['query'],
    },
  },
];

// ---------------------------------------------------------------------------
// Draft-tool schemas (Phase 2)
// Dispatcher returns { kind: 'draft', type, payload } — nothing is written.
// Coach approves the resulting DraftCard to commit via lib/draftCommit.ts.
// ---------------------------------------------------------------------------

export const DRAFT_TOOLS: AnthropicTool[] = [
  {
    name: 'draft_session_plan',
    description: 'Propose a full session plan as a preview card for coach approval. ' +
      'Does NOT save. Use when the coach asks to create/plan/build/draft a session.\n' +
      '\nThe drills MUST fill the whole session — their duration_mins sum to the ' +
      'session duration_mins (within ±5) — and MUST include a SMALL_SIDED_GAME phase ' +
      'before the COOL_DOWN. Never stop at isolated drills leaving time unplanned.',
    input_schema: {
      type: 'object',
      properties: {
        team_id: { type: 'string' },
        date: { type: 'string', description: 'ISO date YYYY-MM-DD' },
        title: { type: 'string' },
        duration_mins: { type: 'number' },
        objectives: { type: 'array', items: { type: 'string' } },
        psych_social_intent: { type: 'string', description: 'Required — the mental/social learning goal' },
        phase_coverage: {
          type: 'object',
          description: 'Primary phase-of-play percentages that sum to 100',
          properties: {
            attacking: { type: 'number' },
            defending: { type: 'number' },
            transitions: { type: 'number' },
            individual: { type: 'number' },
          },
          required: ['attacking', 'defending', 'transitions', 'individual'],
        },
        corner_coverage: {
          type: 'object',
          description: 'Secondary 4-corner percentages that sum to 100',
          properties: {
            technical_tactical: { type: 'number' },
            physical: { type: 'number' },
            psychological: { type: 'number' },
            social: { type: 'number' },
          },
          required: ['technical_tactical', 'physical', 'psychological', 'social'],
        },
        drills: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              description: { type: 'string' },
              drill_type: { type: 'string', description: 'WARM_UP|TECHNICAL|TACTICAL|SMALL_SIDED_GAME|COOL_DOWN' },
              primary_phase: { type: 'string', description: 'attacking|defending|transitions|individual' },
              sub_themes: { type: 'array', items: { type: 'string' } },
              duration_mins: { type: 'number' },
              corner_ids: { type: 'array', items: { type: 'string' } },
              min_players: { type: 'number' },
              max_players: { type: 'number' },
              space_required: { type: 'string' },
              equipment: { type: 'array', items: { type: 'string' } },
              coaching_points: { type: 'array', items: { type: 'string' } },
              progressions: { type: 'array', items: { type: 'string' } },
            },
            required: ['name', 'description', 'primary_phase', 'duration_mins'],
          },
        },
      },
      required: ['date', 'title', 'duration_mins', 'psych_social_intent', 'phase_coverage', 'drills'],
    },
  },
  {
    name: 'draft_drill',
    description:
      'Propose a single drill for the library as a preview card. Does NOT save. ' +
      'When the drill has a clear shape (positions matter, e.g. SSGs, rondos, ' +
      'finishing patterns), include a drill_spec with entities + cones + arrows ' +
      'at normalised [0..1] pitch coordinates so the canvas preview renders. ' +
      'Skip drill_spec for purely conceptual drills (e.g. dynamic warm-ups).',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        description: { type: 'string' },
        drill_type: { type: 'string', description: 'WARM_UP|TECHNICAL|TACTICAL|SMALL_SIDED_GAME|COOL_DOWN' },
        duration_mins: { type: 'number' },
        corner_ids: { type: 'array', items: { type: 'string' } },
        sub_corner_ids: { type: 'array', items: { type: 'string' } },
        primary_corner_id: { type: 'string' },
        min_players: { type: 'number' },
        max_players: { type: 'number' },
        space_required: { type: 'string' },
        equipment: { type: 'array', items: { type: 'string' } },
        coaching_points: { type: 'array', items: { type: 'string' } },
        progressions: { type: 'array', items: { type: 'string' } },
        regressions: { type: 'array', items: { type: 'string' } },
        difficulty: { type: 'string', description: 'FOUNDATION|INTERMEDIATE|ADVANCED' },
        phase_objective: { type: 'string' },
        four_cm_connection: { type: 'string' },
        drill_spec: {
          type: 'object',
          description:
            'Visual layout. Coordinate system: x=0 left touchline, x=1 right; ' +
            'y=0 far/defending end, y=1 near/attacking end. Pick a pitchPresetId ' +
            'matching the drill (4v4 → "4v4", 5v5 → "5v5", 7v7 → "7v7", 9v9 → ' +
            '"9v9", 11v11 → "full_11v11"). All entity ids are stable strings ' +
            'so arrows can reference them.',
          properties: {
            schemaVersion: { type: 'integer', description: 'Always 1.' },
            pitchPresetId: {
              type: 'string',
              description: 'One of: 4v4 | 5v5 | 7v7 | 9v9 | full_11v11',
            },
            entities: {
              type: 'array',
              description: 'Player + ball tokens at normalised positions.',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string', description: 'Stable id (e.g. "h1", "a3", "b").' },
                  type: { type: 'string', description: 'home | away | ball | official' },
                  position: {
                    type: 'array',
                    description: 'Normalised [x, y] in [0..1].',
                    items: { type: 'number' },
                  },
                  label: { type: 'string', description: 'Jersey number or short name; "" for ball.' },
                  role: { type: 'string', description: 'Optional GK/CB/CM/etc.' },
                },
                required: ['id', 'type', 'position', 'label'],
              },
            },
            cones: {
              type: 'array',
              description: 'Passive equipment markers (cones / mannequins / gates / goals).',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  position: { type: 'array', items: { type: 'number' } },
                  variant: {
                    type: 'string',
                    description: 'cone | flat | mannequin | gate | goal_small | goal_full',
                  },
                  rotation: { type: 'number', description: 'Degrees; meaningful for gate/goal.' },
                },
                required: ['id', 'position'],
              },
            },
            arrows: {
              type: 'array',
              description:
                'Movement arrows. from/to anchor either to an existing entity by id ' +
                'or to a fixed normalised point.',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  from: {
                    type: 'object',
                    description: '{ kind: "entity", entityId: "h1" } OR { kind: "point", position: [x, y] }',
                    properties: {
                      kind: { type: 'string' },
                      entityId: { type: 'string' },
                      position: { type: 'array', items: { type: 'number' } },
                    },
                    required: ['kind'],
                  },
                  to: {
                    type: 'object',
                    properties: {
                      kind: { type: 'string' },
                      entityId: { type: 'string' },
                      position: { type: 'array', items: { type: 'number' } },
                    },
                    required: ['kind'],
                  },
                  kind: {
                    type: 'string',
                    description: 'pass | run | dribble | press | cover',
                  },
                  style: { type: 'string', description: 'solid | dashed | dotted' },
                  curvature: { type: 'number', description: '[0..1]; 0 = straight; 0.2-0.4 reads natural.' },
                  label: { type: 'string', description: 'Optional inline cue.' },
                },
                required: ['id', 'from', 'to'],
              },
            },
            zones: {
              type: 'array',
              description: 'Optional rectangular / circular zone markers (press zones, rondo grids).',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  shape: {
                    type: 'object',
                    description:
                      '{ kind: "rect", topLeft: [x, y], bottomRight: [x, y] } OR ' +
                      '{ kind: "circle", center: [x, y], radiusNorm: 0.15 }',
                  },
                  fill: { type: 'string', description: 'CSS colour, e.g. "#C0392B30" with alpha.' },
                  stroke: { type: 'string' },
                  strokeStyle: { type: 'string', description: 'solid | dashed | dotted' },
                  label: { type: 'string' },
                },
                required: ['id', 'shape'],
              },
            },
            keyframes: {
              type: 'array',
              description:
                'Animation steps; empty for a static layout. Each keyframe: ' +
                '{ id, durationSec, positions: { entityId: [x, y] } }.',
              items: { type: 'object' },
            },
          },
          required: ['schemaVersion', 'entities', 'cones', 'arrows', 'zones', 'keyframes'],
        },
      },
      required: ['name', 'description', 'duration_mins', 'corner_ids'],
    },
  },
  {
    name: 'draft_drill_setup',
    description:
      'Set up the drill canvas the coach is editing — pitch sizing, role-based ' +
      'players (Attacker / Defender / Neutral / Bumper / Server / Coach / Goalkeeper), ' +
      'equipment (cones, mannequins, goals, hurdles, etc.), and arrows. The Apply ' +
      'button on the resulting card writes the setup INTO the open drill (it does ' +
      'NOT create a new drill). Use this when the coach asks you to "set up a 4v3 ' +
      'with 2 servers", "make this a half-pitch drill", "add 6 cones around the grid", ' +
      'or similar canvas-level setup. Coordinates: normalised [0..1] for player + cone ' +
      'positions (x=0 left touchline, x=1 right; y=0 far end, y=1 near).',
    input_schema: {
      type: 'object',
      properties: {
        pitch_view: {
          type: 'string',
          description: 'One of: full | half_attacking | half_defending | quarter | custom',
        },
        custom_pitch: {
          type: 'object',
          description: 'When pitch_view = custom — width × length. Unit: metres.',
          properties: {
            width: { type: 'number' },
            length: { type: 'number' },
          },
        },
        target_player_count: {
          type: 'number',
          description: 'Coach\'s intended total drill size (sum of all roles). Displayed in the player tray header.',
        },
        role_players: {
          type: 'array',
          description: 'Each entry is a player to drop on the pitch with a role + position.',
          items: {
            type: 'object',
            properties: {
              role_id: {
                type: 'string',
                description: 'One of: attacker | defender | neutral | bumper | server | coach | goalkeeper',
              },
              position: {
                type: 'array',
                description:
                  'NORMALISED [x, y] in [0..1] OVER THE WORKING AREA — NOT the full pitch. ' +
                  'The working area is whatever pitch_view + custom_pitch define. ' +
                  '[0.5, 0.5] = centre of the working area; [0, 0] = top-left; ' +
                  '[1, 1] = bottom-right. For a 5v3 rondo on a 15×15 m grid, attackers form ' +
                  'the perimeter: [0.5, 0.05], [0.95, 0.5], [0.5, 0.95], [0.05, 0.5], ' +
                  '[0.5, 0.5] (the pivot). Defenders sit inside the grid.',
                items: { type: 'number', minimum: 0, maximum: 1 },
                minItems: 2,
                maxItems: 2,
              },
              label: {
                type: 'string',
                description: 'Optional jersey number or short identifier (e.g. "9", "GK"). Defaults to the role\'s glyph.',
              },
            },
            required: ['role_id', 'position'],
          },
        },
        cones: {
          type: 'array',
          description: 'Equipment to place — cones, mannequins, goals, hurdles, etc.',
          items: {
            type: 'object',
            properties: {
              variant: {
                type: 'string',
                description:
                  'cone | flat | cone_disc | cone_training | cone_traffic | mannequin | pole | ' +
                  'gate | corner_flag | goal_small | goal_mini | goal_7v7 | goal_9v9 | goal_full | ' +
                  'crossbar | hurdle_mini | hurdle_med | agility_ladder | bench | rebounder',
              },
              position: {
                type: 'array',
                description:
                  'NORMALISED [x, y] in [0..1] OVER THE WORKING AREA — NOT the full pitch. ' +
                  'The working area is whatever pitch_view + custom_pitch define. ' +
                  '[0.5, 0.5] = centre of the working area; [0, 0] = top-left; ' +
                  '[1, 1] = bottom-right. For a 5v3 rondo on a 15×15 m grid, attackers form ' +
                  'the perimeter: [0.5, 0.05], [0.95, 0.5], [0.5, 0.95], [0.05, 0.5], ' +
                  '[0.5, 0.5] (the pivot). Defenders sit inside the grid.',
                items: { type: 'number', minimum: 0, maximum: 1 },
                minItems: 2,
                maxItems: 2,
              },
              rotation: {
                type: 'number',
                description: 'Optional rotation in degrees (0..360). Goals default to 180.',
              },
            },
            required: ['variant', 'position'],
          },
        },
        arrows: {
          type: 'array',
          description:
            'Movement arrows showing how the play unfolds — passes, runs, dribbles, ' +
            'pressing trajectories. A drill without arrows is useless to a coach; ' +
            'ALWAYS include arrows that depict the intended play, even if just 2-4 ' +
            'arrows for a small rondo. Endpoints MUST fall inside the working area.',
          items: {
            type: 'object',
            properties: {
              from: {
                type: 'array',
                description: 'NORMALISED [x, y] in [0..1] where the arrow starts.',
                items: { type: 'number', minimum: 0, maximum: 1 },
                minItems: 2, maxItems: 2,
              },
              to: {
                type: 'array',
                description: 'NORMALISED [x, y] in [0..1] where the arrow ends.',
                items: { type: 'number', minimum: 0, maximum: 1 },
                minItems: 2, maxItems: 2,
              },
              kind: {
                type: 'string',
                description: 'pass | run | dribble | press | cover',
              },
              label: {
                type: 'string',
                description: 'Optional short label rendered near the arrow midpoint.',
              },
            },
            required: ['from', 'to', 'kind'],
          },
        },
        ball: {
          type: 'object',
          description:
            'Where the ball starts. ALWAYS include for any drill involving possession, ' +
            'passing, finishing, or pressing. Skip only for pure-physical drills (ladder, ' +
            'agility, fitness circuits).',
          properties: {
            position: {
              type: 'array',
              description: 'NORMALISED [x, y] in [0..1]. Must be inside the working area.',
              items: { type: 'number', minimum: 0, maximum: 1 },
              minItems: 2, maxItems: 2,
            },
          },
          required: ['position'],
        },
        organisation: {
          type: 'string',
          description:
            'How the drill is set up — markdown OK. Coach reads this to understand the ' +
            'rules, space, conditions. 3-6 short bullet points (markdown "-" lists).',
        },
        coaching_points: {
          type: 'array',
          description:
            'What to observe + coach DURING the drill. 3-5 specific, actionable cues.',
          items: { type: 'string' },
        },
        // -- Drill metadata. ALWAYS emit these so the library card +
        // detail page render with the same richness as platform drills.
        name: {
          type: 'string',
          description: 'Concise drill name (e.g. "5v3 rondo — 15m grid"). Used as the row title.',
        },
        description: {
          type: 'string',
          description:
            'One-paragraph summary of what the drill develops. 2-3 sentences. ' +
            'Reads on the library card under the name.',
        },
        drill_type: {
          type: 'string',
          description: 'WARM_UP | TECHNICAL | TACTICAL | SMALL_SIDED_GAME | COOL_DOWN',
        },
        duration_mins: { type: 'number', description: 'Suggested drill duration in minutes.' },
        difficulty: { type: 'string', description: 'FOUNDATION | INTERMEDIATE | ADVANCED' },
        min_players: { type: 'number' },
        max_players: { type: 'number' },
        space_required: { type: 'string', description: 'e.g. "15×15 m grid" or "half pitch".' },
        equipment: {
          type: 'array',
          description: 'Equipment names — coach-facing labels like "cones", "bibs", "balls", "mannequins". 3-6 items typical.',
          items: { type: 'string' },
        },
        corner_ids: {
          type: 'array',
          description:
            'FA 4 Corner ids the drill develops. Pick 1-3 from: ' +
            'corner-technical | corner-physical | corner-psych | corner-social',
          items: { type: 'string' },
        },
        primary_corner_id: {
          type: 'string',
          description: 'Primary corner — one of corner-technical / corner-physical / corner-psych / corner-social.',
        },
        sub_themes: {
          type: 'array',
          description: 'Phase-of-play sub-themes (e.g. "rondo", "pressing trigger", "build-up"). 1-3 items.',
          items: { type: 'string' },
        },
        primary_phase: {
          type: 'string',
          description: 'attacking | defending | transitions | individual',
        },
        progressions: {
          type: 'array',
          description: 'Ways to make the drill HARDER (1-3 items).',
          items: { type: 'string' },
        },
        regressions: {
          type: 'array',
          description: 'Ways to make the drill EASIER (1-3 items).',
          items: { type: 'string' },
        },
        keyframes: {
          type: 'array',
          description:
            'STRONGLY RECOMMENDED — multi-phase animation depicting the ' +
            'play. Emit 2-4 keyframes for ANY drill where the ball moves ' +
            '(rondo, possession, pattern play, finishing). Each keyframe ' +
            'snapshots player + ball positions AT THAT MOMENT in the play. ' +
            'For a 5v3 rondo: kf1 = ball with A1 (start), kf2 = ball with ' +
            'A2 (after pass 1), kf3 = ball with A3 (after pass 2). ' +
            'Players that DO move (the ball, plus the attacker who will ' +
            'receive next) must be in role_players[]; static players can ' +
            'be omitted from a kf to hold their previous position. Label ' +
            'must EXACTLY match the placement label (e.g. "A1", "D2").',
          items: {
            type: 'object',
            properties: {
              label: { type: 'string', description: 'Short label — e.g. "Pass 1", "Press triggered".' },
              durationSec: { type: 'number', description: 'Time to transition INTO this keyframe from the previous. First keyframe = 0.' },
              role_players: {
                type: 'array',
                description: 'End positions for role players at this keyframe. Match by label.',
                items: {
                  type: 'object',
                  properties: {
                    label: { type: 'string', description: 'e.g. "A1", "D2"' },
                    position: {
                      type: 'array',
                      description: 'NORMALISED [x, y] in [0..1] OVER THE WORKING AREA.',
                      items: { type: 'number', minimum: 0, maximum: 1 },
                      minItems: 2, maxItems: 2,
                    },
                  },
                  required: ['label', 'position'],
                },
              },
              ball: {
                type: 'object',
                description: 'Ball position at this keyframe.',
                properties: {
                  position: {
                    type: 'array',
                    items: { type: 'number', minimum: 0, maximum: 1 },
                    minItems: 2, maxItems: 2,
                  },
                },
              },
            },
            required: ['durationSec'],
          },
        },
        rationale: {
          type: 'string',
          description: 'One-sentence explanation of the setup so the coach knows why these positions / numbers were chosen.',
        },
      },
      required: ['rationale'],
    },
  },
  {
    name: 'draft_canvas_patch',
    description:
      'Mutate the drill currently open in the canvas editor by emitting a list of typed ops. ' +
      'Use this when the coach is editing an existing drill\'s layout via voice/chat — ' +
      'e.g. "move the right midfielder up the pitch", "add another defender", ' +
      '"draw a press arrow from h2 to a1". Each op applies in order against the live canvas; ' +
      'partial failures are non-fatal. Coordinate system: x=0 left touchline, x=1 right; ' +
      'y=0 far end, y=1 near. Reuse stable entity ids when present (h1, a3, b, etc.) — ' +
      'find them in the screen context\'s drill_spec, never invent. Skip this tool when ' +
      'the coach is asking a question or wants a new drill from scratch (use draft_drill ' +
      'for new drills). v1 ops: move_entity, add_entity, remove_entity, add_arrow, remove_arrow.',
    input_schema: {
      type: 'object',
      properties: {
        ops: {
          type: 'array',
          description: 'Ordered list of mutations. Each item is one of the v1 op shapes.',
          items: {
            type: 'object',
            properties: {
              op: {
                type: 'string',
                description:
                  'move_entity | add_entity | remove_entity | add_arrow | remove_arrow',
              },
              entity_id: {
                type: 'string',
                description: 'For move_entity / remove_entity. Stable id of the target entity.',
              },
              to: {
                type: 'array',
                description:
                  'For move_entity. Target [x, y] in [0..1]. ' +
                  'Sensible nudges: "up the pitch" = y+0.1; "wider" = x±0.15.',
                items: { type: 'number' },
              },
              entity: {
                type: 'object',
                description:
                  'For add_entity. Same shape as DrillEntity: id, type ' +
                  '(home|away|ball|official), position [x, y], label, optional role.',
                properties: {
                  id: { type: 'string' },
                  type: { type: 'string' },
                  position: { type: 'array', items: { type: 'number' } },
                  label: { type: 'string' },
                  role: { type: 'string' },
                },
              },
              arrow: {
                type: 'object',
                description:
                  'For add_arrow. id is required; from/to anchor either to ' +
                  'an existing entity ({kind:"entity", entityId}) or to a fixed ' +
                  'point ({kind:"point", position:[x,y]}). kind: pass|run|dribble|press|cover.',
                properties: {
                  id: { type: 'string' },
                  from: { type: 'object' },
                  to: { type: 'object' },
                  kind: { type: 'string' },
                  style: { type: 'string' },
                  curvature: { type: 'number' },
                  label: { type: 'string' },
                },
              },
              arrow_id: {
                type: 'string',
                description: 'For remove_arrow. Stable id of the target arrow.',
              },
            },
            required: ['op'],
          },
        },
        rationale: {
          type: 'string',
          description:
            'One-sentence explanation of what changed, in the coach\'s voice. ' +
            'The UI plays this back via TTS so the coach hears confirmation without looking.',
        },
      },
      required: ['ops'],
    },
  },
  {
    name: 'draft_development_note',
    description: 'Propose a player development entry as a preview card. Does NOT save. ' +
      'corner_id must be a UUID — get it from get_ratings or get_player results, never invent.',
    input_schema: {
      type: 'object',
      properties: {
        player_id: { type: 'string' },
        corner_id: { type: 'string', description: 'UUID from get_ratings results' },
        sub_corner_id: { type: 'string' },
        period: { type: 'string', description: 'WEEK|MONTH|SEASON' },
        coach_notes: { type: 'string' },
        self_assessment: { type: 'string' },
      },
      required: ['player_id', 'corner_id', 'period', 'coach_notes'],
    },
  },
  {
    name: 'draft_memory',
    description: 'Propose a durable fact to store in long-term memory as a preview card. Does NOT save. ' +
      'Use when the coach has told you a preference, philosophy, or trait that should survive this conversation ' +
      '(e.g. "I always want a 15-min dynamic warm-up"). Never silently remember — always draft for approval.',
    input_schema: {
      type: 'object',
      properties: {
        scope: { type: 'string', description: 'coach | team | player | session | drill' },
        entity_id: { type: 'string', description: 'UUID when scope is player/session/drill' },
        kind: { type: 'string', description: 'preference | philosophy | trait | pattern | reflection' },
        fact: { type: 'string', description: 'One-sentence durable statement in the coach\'s voice' },
        tags: { type: 'array', items: { type: 'string' }, description: 'e.g. ["corner:psychological","topic:warmup"]' },
      },
      required: ['scope', 'kind', 'fact'],
    },
  },
  {
    name: 'draft_drill_grade',
    description: 'Propose a grade for a drill run in a session. Does NOT save. ' +
      'session_drill_id must come from get_session_review_context — never invent. ' +
      'Sentiment is the primary signal (worked / mixed / struggled); grade 1-5 is optional and secondary.',
    input_schema: {
      type: 'object',
      properties: {
        session_review_id: { type: 'string', description: 'From get_session_review_context.review.id' },
        session_drill_id: { type: 'string', description: 'From get_session_review_context.drills[i].session_drill_id' },
        sentiment: { type: 'string', description: 'worked | mixed | struggled' },
        grade: { type: 'number', description: 'Optional 1-5 quantitative grade' },
        coach_notes: { type: 'string' },
        reasoning: { type: 'string', description: 'One-sentence rationale shown in the DraftCard' },
      },
      required: ['session_review_id', 'session_drill_id', 'sentiment'],
    },
  },
  {
    name: 'draft_player_grade',
    description: 'Propose per-corner grades (0-100) for one player in a session. Does NOT save. ' +
      'corner ids come from get_session_review_context.corners[] — never invent them. ' +
      'Under blind-rating (§07) these land as COACH_SUBMITTED and are revealed once the player submits theirs.',
    input_schema: {
      type: 'object',
      properties: {
        session_id: { type: 'string' },
        player_id: { type: 'string' },
        corner_scores: {
          type: 'object',
          description: 'Map of corner_id (UUID from context) → score 0-100',
          additionalProperties: { type: 'number' },
        },
        reasoning: { type: 'string' },
      },
      required: ['session_id', 'player_id', 'corner_scores'],
    },
  },
  {
    name: 'draft_session_review',
    description: 'Propose session-level review text (what worked, issues, next-session tweaks, AI narrative). ' +
      'Does NOT save. Use once enough drill/player grading is done that the narrative has substance.',
    input_schema: {
      type: 'object',
      properties: {
        session_review_id: { type: 'string' },
        notes_working: { type: 'string' },
        notes_issues: { type: 'string' },
        next_session_tweaks: { type: 'string', description: 'Gets fed into the next session generator verbatim — keep specific and actionable' },
        ai_narrative: { type: 'string', description: '3-4 sentence post-match narrative per spec §08.2' },
      },
      required: ['session_review_id'],
    },
  },
  {
    name: 'draft_curriculum',
    description: 'Propose a full season curriculum as a preview card. Does NOT save. ' +
      'Use when the coach asks to build / generate / draft the curriculum. Needs the ' +
      "team's context — call get_team + get_principles first so phase_plans reflect the " +
      'real age group, formation, and principles.\n' +
      '\nRULES:\n' +
      '- Divide the season into phase_plans: Pre-season, Early season, Mid-season, Late season ' +
      '(and Playoffs if the season is long). Each phase has a theme.\n' +
      '- corner_emphasis on each phase must sum to 100 across technical_tactical/physical/' +
      'psychological/social.\n' +
      '- Every phase must include at least some psychological + social emphasis (Psych-Social ' +
      'Foundation Rule, §04).\n' +
      '- Each weekly_topic has a date_start (Monday, YYYY-MM-DD), a primary_corner, sub_corners, ' +
      'session_count, principle_connection linking the week to one of the coach\'s principles.\n' +
      '\nCOMPLETENESS (CRITICAL):\n' +
      '- total_weeks MUST equal the sum of weekly_topics across all phase_plans. Do NOT emit\n' +
      '  phase_plans with an empty weekly_topics array — if a phase has 6 weeks, list 6 weekly\n' +
      '  topics with distinct dates. The view renders off weekly_topics; an empty array means\n' +
      '  the coach sees "N weeks total" but no actual plan.\n' +
      '- Keep each weekly_topic compact (topic + principle_connection under ~15 words each);\n' +
      '  rich prose lives in the phase theme, not the weeklies. Tight output fits the token\n' +
      '  budget for a full 20-30 week season in one turn.\n' +
      '- Cover the WHOLE season from team.season_start to team.season_end inclusive. Do not\n' +
      '  truncate mid-season because you are running out of tokens — if tight, shorten prose\n' +
      '  not week count.',
    input_schema: {
      type: 'object',
      properties: {
        team_id: { type: 'string' },
        season_overview: { type: 'string', description: '2-3 sentences of narrative context' },
        total_weeks: { type: 'number' },
        what_to_expect: { type: 'string', description: 'Coach-facing summary (2-4 sentences) of what to expect over the planned weeks — the arc, what they\'ll see on the pitch, and why.' },
        phase_plans: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              phase: { type: 'string', description: 'Pre-season | Early season | Mid-season | Late season | Playoffs' },
              weeks: { type: 'number' },
              theme: { type: 'string' },
              rationale: { type: 'string', description: 'Why this theme now — 1-2 sentences in the coach\'s voice.' },
              corner_emphasis: {
                type: 'object',
                properties: {
                  technical_tactical: { type: 'number' },
                  physical: { type: 'number' },
                  psychological: { type: 'number' },
                  social: { type: 'number' },
                },
                required: ['technical_tactical', 'physical', 'psychological', 'social'],
              },
              weekly_topics: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    week_number: { type: 'number' },
                    date_start: { type: 'string', description: 'Monday of the week, YYYY-MM-DD' },
                    topic: { type: 'string' },
                    primary_corner: { type: 'string', description: 'technical_tactical | physical | psychological | social' },
                    sub_corners: { type: 'array', items: { type: 'string' } },
                    session_count: { type: 'number' },
                    principle_connection: { type: 'string' },
                    intensity: { type: 'string', description: 'low | medium | high' },
                  },
                  required: ['week_number', 'date_start', 'topic', 'primary_corner', 'session_count'],
                },
              },
            },
            required: ['phase', 'weeks', 'theme', 'corner_emphasis', 'weekly_topics'],
          },
        },
      },
      required: ['season_overview', 'total_weeks', 'phase_plans'],
    },
  },
  {
    name: 'draft_principle',
    description: 'Propose a coaching principle for the team\'s philosophy. Does NOT save. ' +
      '\n\nEVERY principle MUST tag at least one of the four 4CM corners in corner_ids ' +
      '— no exceptions. Use these exact ids:\n' +
      '  corner-technical  (T — Technical/Tactical)\n' +
      '  corner-physical   (P — Physical)\n' +
      '  corner-psych      (Y — Psychological)\n' +
      '  corner-social     (S — Social)\n' +
      '\nTag every corner the principle genuinely develops — a pressing principle for ' +
      'example usually tags Technical + Physical + Psychological. Do not pick just one ' +
      'when more apply.\n' +
      '\nThe Psych-Social Foundation Rule requires the OVERALL principle set (across ' +
      'all drafts you produce in this conversation) to include ≥1 principle tagged ' +
      'corner-psych or corner-social. Err on the side of over-tagging psych/social; the ' +
      'coach can untick later.',
    input_schema: {
      type: 'object',
      properties: {
        team_id: { type: 'string' },
        type: { type: 'string', description: 'ATTACK|DEFEND|TRANSITION|RESTART' },
        text: { type: 'string' },
        corner_ids: {
          type: 'array',
          items: { type: 'string' },
          description: 'One or more of: corner-technical, corner-physical, corner-psych, corner-social. Never empty.',
        },
        phase: { type: 'string' },
      },
      required: ['type', 'text', 'corner_ids'],
    },
  },
  {
    name: 'draft_player_review',
    description: 'Propose a short-term multidisciplinary review for ONE player. ' +
      'Does NOT save. Use when the coach asks to "review Kai" / "draft Ada\'s ' +
      'development plan" / etc. Call get_player first to see recent ratings + ' +
      'profile — the review should reference them.\n' +
      '\nTEMPLATE GATING (critical):\n' +
      '- Pick template_variant from: foundation1 (U7-U8), foundation2 (U9-U10), ' +
      'foundation3 (U11-U12), progression1 (U13-U14), progression2 (U15-U16), ' +
      'progression3 (U17-U18), performance (U19+). Align to player age.\n' +
      '- If template does not include s_and_c (Foundation tiers), DO NOT provide ' +
      's_and_c bullets. Same for individual / contextual — populate only columns ' +
      'that exist in the chosen template.\n' +
      '- Max bullets per column: foundation1=1, foundation2-3=2, progression1=3, ' +
      'progression2-3 + performance=4. Never exceed.\n' +
      '- Tone: foundation1-2 parent-friendly (no jargon). Progression+ technical.\n' +
      '\nFOCUS AREAS keys: technical | tactical | physical | psycho_social | ' +
      's_and_c | individual | contextual. Each maps to string[]. Missing keys ' +
      'mean "not applicable for this template".',
    input_schema: {
      type: 'object',
      properties: {
        player_id: { type: 'string' },
        template_variant: {
          type: 'string',
          description: 'foundation1|foundation2|foundation3|progression1|progression2|progression3|performance',
        },
        review_number: { type: 'number' },
        period_start: { type: 'string', description: 'YYYY-MM-DD' },
        period_end: { type: 'string', description: 'YYYY-MM-DD' },
        focus_areas: {
          type: 'object',
          properties: {
            technical:     { type: 'array', items: { type: 'string' } },
            tactical:      { type: 'array', items: { type: 'string' } },
            physical:      { type: 'array', items: { type: 'string' } },
            psycho_social: { type: 'array', items: { type: 'string' } },
            s_and_c:       { type: 'array', items: { type: 'string' } },
            individual:    { type: 'array', items: { type: 'string' } },
            contextual:    { type: 'array', items: { type: 'string' } },
          },
        },
        parent_summary: { type: 'string' },
      },
      required: ['player_id', 'template_variant', 'period_start', 'period_end', 'focus_areas'],
    },
  },
];

/** All tools exposed to the Brain (read + draft + tactics). */
export const BRAIN_TOOLS: AnthropicTool[] = [
  ...READ_TOOLS,
  ...DRAFT_TOOLS,
  ...TACTICS_TOOLS,
];

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

function resolveTeam(input: any, ctx: BrainToolCtx): string | null {
  return input?.team_id ?? ctx.teamId ?? null;
}

function clamp(n: number | undefined, def: number, max: number): number {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return def;
  return Math.min(n, max);
}

export function makeBrainDispatcher(ctx: BrainToolCtx): ToolDispatcher {
  return async (name, rawInput) => {
    const input = (rawInput ?? {}) as any;

    switch (name) {
      case 'get_team': {
        const id = resolveTeam(input, ctx);
        if (!id) return { error: 'no active team' };
        const { data, error } = await supabase.from('teams')
          .select('id, name, age_group, proficiency_level, formation_default, season_type, season_start, season_end, training_days_per_week, session_duration_mins, player_count')
          .eq('id', id).maybeSingle();
        if (error) return { error: error.message };
        return data ?? { error: 'not found' };
      }

      case 'list_players': {
        const id = resolveTeam(input, ctx);
        if (!id) return { error: 'no active team' };
        const { data, error } = await supabase.from('team_players')
          .select('player_id, position, jersey_number, is_active, users(name, dob, player_profile_pct)')
          .eq('team_id', id).eq('is_active', true).limit(60);
        if (error) return { error: error.message };
        const rows = (data ?? []).map((row: any) => ({
          id: row.player_id,
          name: row.users?.name ?? '(unknown)',
          position: row.position,
          jersey_number: row.jersey_number,
          dob: row.users?.dob,
          profile_pct: (row.users?.player_profile_pct as number) ?? 0,
        }));
        // Summary hint so Brain can decide personalise-vs-generalise
        // without always fetching get_player for each row. Low avg →
        // generalise; high avg → you can reference specifics.
        const avg = rows.length
          ? Math.round(rows.reduce((s, r) => s + r.profile_pct, 0) / rows.length)
          : 0;
        const deep = rows.filter(r => r.profile_pct >= 75).map(r => r.name);
        const quick = rows.filter(r => r.profile_pct < 15).map(r => r.name);
        return {
          players: rows,
          roster_depth_summary: {
            avg_profile_pct: avg,
            deep_count: deep.length,
            deep_names: deep.slice(0, 6),
            quick_only_count: quick.length,
            quick_only_names: quick.slice(0, 6),
          },
        };
      }

      case 'get_player': {
        const pid = input.player_id;
        if (!pid) return { error: 'player_id required' };
        const [userRes, tpRes, ratingsRes, devRes] = await Promise.all([
          supabase.from('users').select('id, name, dob, bio_age, player_profile, player_profile_pct').eq('id', pid).maybeSingle(),
          supabase.from('team_players').select('team_id, position, jersey_number').eq('player_id', pid).maybeSingle(),
          supabase.from('ratings').select('corner_id, score, created_at').eq('ratee_id', pid).order('created_at', { ascending: false }).limit(40),
          supabase.from('development_entries').select('corner_id, period, coach_notes, self_assessment, created_at').eq('player_id', pid).order('created_at', { ascending: false }).limit(5),
        ]);

        const ratings = ratingsRes.data ?? [];
        const byCorner: Record<string, { count: number; sum: number }> = {};
        for (const r of ratings) {
          const k = r.corner_id as string;
          byCorner[k] = byCorner[k] ?? { count: 0, sum: 0 };
          byCorner[k].count += 1;
          byCorner[k].sum += r.score;
        }
        const corner_averages = Object.fromEntries(
          Object.entries(byCorner).map(([k, v]) => [k, Math.round(v.sum / v.count)])
        );

        return {
          user: userRes.data,
          team_player: tpRes.data,
          ratings_recent: ratings.slice(0, 10),
          corner_averages,
          development_entries_recent: devRes.data ?? [],
          // Profile depth — Brain should use the tiered blob to personalise.
          // Empty / low pct → stick to general advice; high pct → reference
          // baseline scores, strengths, dev areas etc. verbatim.
          profile: (userRes.data as any)?.player_profile ?? {},
          profile_pct: ((userRes.data as any)?.player_profile_pct as number) ?? 0,
        };
      }

      case 'list_sessions': {
        const id = resolveTeam(input, ctx);
        if (!id) return { error: 'no active team' };
        let q = supabase.from('sessions')
          .select('id, date, title, duration_mins, status, curriculum_week, objectives, psych_social_intent')
          .eq('team_id', id)
          .order('date', { ascending: false })
          .limit(clamp(input.limit, 10, 50));
        if (input.from) q = q.gte('date', input.from);
        if (input.to) q = q.lte('date', input.to);
        const { data, error } = await q;
        if (error) return { error: error.message };
        return data ?? [];
      }

      case 'get_session': {
        const sid = input.session_id;
        if (!sid) return { error: 'session_id required' };
        const [sessionRes, drillsRes] = await Promise.all([
          supabase.from('sessions').select('*').eq('id', sid).maybeSingle(),
          supabase.from('session_drills')
            .select('sort_order, drills(id, name, description, drill_type, duration_mins, corner_ids, coaching_points, progressions, min_players, max_players, equipment, space_required)')
            .eq('session_id', sid)
            .order('sort_order', { ascending: true }),
        ]);
        return {
          session: sessionRes.data,
          drills: (drillsRes.data ?? []).map((row: any) => ({ sort_order: row.sort_order, ...row.drills })),
        };
      }

      case 'list_drills': {
        let q = supabase.from('drills')
          .select('id, name, drill_type, duration_mins, corner_ids, min_players, max_players, space_required')
          .is('deleted_at', null)
          .order('name', { ascending: true })
          .limit(clamp(input.limit, 15, 40));
        if (input.drill_type) q = q.eq('drill_type', input.drill_type);
        if (input.corner_id) q = q.contains('corner_ids', [input.corner_id]);
        if (input.search) q = q.or(`name.ilike.%${input.search}%,description.ilike.%${input.search}%`);
        const { data, error } = await q;
        if (error) return { error: error.message };
        return data ?? [];
      }

      case 'get_ratings': {
        const pid = input.player_id;
        if (!pid) return { error: 'player_id required' };
        let q = supabase.from('ratings')
          .select('id, corner_id, sub_corner_id, score, context, session_id, game_id, created_at')
          .eq('ratee_id', pid)
          .order('created_at', { ascending: false })
          .limit(60);
        if (input.corner_id) q = q.eq('corner_id', input.corner_id);
        if (input.since) q = q.gte('created_at', input.since);
        const { data, error } = await q;
        if (error) return { error: error.message };
        const rows = data ?? [];
        const byCorner: Record<string, number[]> = {};
        for (const r of rows) {
          byCorner[r.corner_id] = byCorner[r.corner_id] ?? [];
          byCorner[r.corner_id].push(r.score);
        }
        const averages = Object.fromEntries(
          Object.entries(byCorner).map(([k, arr]) => [k, Math.round(arr.reduce((a, b) => a + b, 0) / arr.length)])
        );
        return { count: rows.length, averages, rows: rows.slice(0, 30) };
      }

      case 'list_video_tags': {
        let q = supabase.from('video_tags')
          .select('id, clip_url, platform, player_id, session_id, corner_id, ts_in, ts_out, coach_note, is_highlight, created_at')
          .order('created_at', { ascending: false })
          .limit(clamp(input.limit, 10, 30));
        if (input.player_id) q = q.eq('player_id', input.player_id);
        if (input.session_id) q = q.eq('session_id', input.session_id);
        if (input.since) q = q.gte('created_at', input.since);
        const { data, error } = await q;
        if (error) return { error: error.message };
        return data ?? [];
      }

      case 'get_curriculum': {
        const id = resolveTeam(input, ctx);
        if (!id) return { error: 'no active team' };
        const { data, error } = await supabase.from('curriculum_plans')
          .select('id, season_start, season_end, plan_data, is_active, generated_at')
          .eq('team_id', id).eq('is_active', true)
          .order('generated_at', { ascending: false }).limit(1).maybeSingle();
        if (error) return { error: error.message };
        return data ?? { error: 'no active curriculum' };
      }

      case 'get_principles': {
        const id = resolveTeam(input, ctx);
        if (!id) return { error: 'no active team' };
        const { data, error } = await supabase.from('principles')
          .select('id, type, text, corner_ids, phase')
          .eq('team_id', id).is('deleted_at', null)
          .order('type', { ascending: true });
        if (error) return { error: error.message };
        // Group by type for compactness
        const grouped: Record<string, any[]> = {};
        for (const p of data ?? []) {
          grouped[p.type] = grouped[p.type] ?? [];
          grouped[p.type].push({ id: p.id, text: p.text, corner_ids: p.corner_ids, phase: p.phase });
        }
        return grouped;
      }

      case 'get_wellness': {
        const pid = input.player_id;
        if (!pid) return { error: 'player_id required' };
        let q = supabase.from('wellness_logs')
          .select('date, sleep_hrs, hydration, mood, soreness, rpe, notes')
          .eq('player_id', pid)
          .order('date', { ascending: false })
          .limit(14);
        if (input.since) q = q.gte('date', input.since);
        const { data, error } = await q;
        if (error) return { error: error.message };
        return data ?? [];
      }

      case 'get_session_review_context': {
        const sid = input.session_id;
        if (!sid) return { error: 'session_id required' };
        // Parallel fan-out: session + drills (with session_drill ids),
        // roster, attendance, review row, corners, coach grades so far,
        // player self-feedback, existing drill grades so far.
        const [sessRes, sdRes, rostRes, attRes, revRes, cornersRes, gradesRes, sfRes] = await Promise.all([
          supabase.from('sessions')
            .select('id, team_id, date, objectives, phase_coverage, corner_coverage, psych_social_intent')
            .eq('id', sid).maybeSingle(),
          supabase.from('session_drills')
            .select('id, sort_order, drills(id, name, drill_type, primary_phase, sub_themes, duration_mins, corner_ids)')
            .eq('session_id', sid)
            .order('sort_order', { ascending: true }),
          supabase.from('team_players')
            .select('player_id, position, jersey_number, users(name)')
            .eq('is_active', true),
          supabase.from('session_attendance')
            .select('player_id, present, reason')
            .eq('session_id', sid),
          supabase.from('session_reviews')
            .select('id, status, notes_working, notes_issues, next_session_tweaks, ai_narrative')
            .eq('session_id', sid).maybeSingle(),
          supabase.from('corners').select('id, label, color_hex, sort_order').order('sort_order'),
          supabase.from('ratings')
            .select('ratee_id, corner_id, score')
            .eq('rater_id', ctx.coachId)
            .eq('ratee_type', 'PLAYER')
            .eq('context', 'SESSION')
            .eq('session_id', sid),
          supabase.from('player_self_feedback')
            .select('player_id, free_text, submitted_at')
            .eq('session_id', sid),
        ]);

        // Narrow team_players to the session's team.
        const teamId = (sessRes.data as any)?.team_id;
        const roster = (rostRes.data ?? []).map((r: any) => ({
          player_id: r.player_id,
          name: r.users?.name ?? '(unknown)',
          position: r.position,
          jersey_number: r.jersey_number,
        }));

        return {
          session: sessRes.data,
          team_id: teamId,
          drills: (sdRes.data ?? []).map((r: any) => ({
            session_drill_id: r.id,
            sort_order: r.sort_order,
            ...(Array.isArray(r.drills) ? r.drills[0] : r.drills),
          })),
          roster,
          attendance: attRes.data ?? [],
          review: revRes.data,
          corners: (cornersRes.data ?? []).map((c: any) => ({
            id: c.id, label: c.label, color_hex: c.color_hex, sort_order: c.sort_order,
          })),
          coach_grades_so_far: gradesRes.data ?? [],
          player_self_feedback: sfRes.data ?? [],
        };
      }

      case 'search_memories': {
        const rows = await searchMemories({
          coach_id: ctx.coachId,
          team_id: ctx.teamId,
          scope: input.scope,
          entity_id: input.entity_id,
          query: input.query,
          tags: input.tags,
          limit: clamp(input.limit, 8, 20),
        });
        return rows.map(r => ({
          id: r.id,
          scope: r.scope,
          entity_id: r.entity_id,
          kind: r.kind,
          fact: r.fact,
          tags: r.tags,
          confidence: r.confidence,
          updated_at: r.updated_at,
        }));
      }

      case 'search_knowledge': {
        const hits = await searchKnowledge(input.query ?? '', {
          coachId: ctx.coachId,
          matchCount: clamp(input.match_count, 5, 10),
        });
        // Compact shape — content is the expensive field; trim to ~600
        // chars per hit so multi-call chats don't bloat the tool result.
        return hits.map((h) => ({
          cite: h.cite,
          source: h.sourceSlug,
          text: h.content.length > 600 ? h.content.slice(0, 600) + '…' : h.content,
          score: Number(h.score.toFixed(3)),
        }));
      }

      // ---- Draft tools: return payload, don't write ----
      case 'draft_session_plan':
      case 'draft_drill':
      case 'draft_drill_setup':
      case 'draft_canvas_patch':
      case 'draft_development_note':
      case 'draft_principle':
      case 'draft_memory':
      case 'draft_drill_grade':
      case 'draft_player_grade':
      case 'draft_session_review':
      case 'draft_curriculum':
      case 'draft_player_review': {
        const payload: any = { ...input };
        if (
          (name === 'draft_session_plan' ||
            name === 'draft_principle' ||
            name === 'draft_curriculum') &&
          !payload.team_id
        ) {
          payload.team_id = ctx.teamId;
        }
        return { kind: 'draft', type: name, payload };
      }

      // ---- Tactics tools (Phase 1 of tactics-hub roadmap) ----
      case 'draft_tactics_pattern': {
        // Pure draft, like the other draft_* tools. The client-side
        // useBrainTacticsDraft hook captures this and writes it through
        // saveTacticsLayout — no DB write here.
        return { kind: 'draft', type: name, payload: { ...input } };
      }
      case 'critique_tactics_pattern': {
        const layout = ctx.getTacticsLayout?.() ?? null;
        if (!layout) {
          return {
            error:
              'no active tactics layout in screen context — coach must open the board first',
          };
        }
        return {
          home_formation: layout.homeFormation,
          away_formation: layout.awayFormation,
          home_player_count: layout.homeSpots.length,
          away_player_count: layout.awaySpots.length,
          arrow_count: layout.arrows.length,
          geometry: critiqueTactics(layout),
        };
      }
      case 'draft_player_briefs': {
        const layout = ctx.getTacticsLayout?.() ?? null;
        const payload = dispatchPlayerBriefsTool(input, layout);
        if (!payload) {
          return {
            error:
              'no active tactics layout in screen context — coach must open the board first',
          };
        }
        return { kind: 'draft', type: name, payload };
      }

      default: {
        // Phase 4 — pattern library tools dispatch through their own
        // module so the storage adapter stays out of this central
        // switch. dispatchPatternTool returns null when `name` isn't
        // one of its tools, letting us fall through to the real
        // unknown-tool error.
        const patternResult = dispatchPatternTool(
          name,
          input,
          ctx.getTacticsPatternStore?.() ?? null,
        );
        if (patternResult !== null) return patternResult;
        return { error: `unknown tool: ${name}` };
      }
    }
  };
}
